import { encodePeriodValue } from '@components'

import { useCallback, useMemo } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { PeriodMode } from '../../../enums/periodMode'
import { useQueryParams } from '../../../hooks/useQueryParams'
import type { PeriodValue } from '../../../types/periodValue'
import type { ReviewEntriesMonthDateRange } from '../types/reviewEntriesMonthPeriod'
import {
  getCurrentReviewEntriesPeriodValue,
  getReviewEntriesDateRangeFromEncodedPeriod,
  getReviewEntriesPeriodFromUrl,
} from '../utils/reviewEntriesMonthPeriod'

interface ReviewEntriesPeriodQuery {
  period: string
}

interface UseReviewEntriesMonthPeriodResult {
  dateRange: ReviewEntriesMonthDateRange
  encodedPeriod: string
  setPeriod: (value: PeriodValue) => boolean
}

export const useReviewEntriesMonthPeriod = (): UseReviewEntriesMonthPeriodResult => {
  const { organization } = useUserOrganization()

  const defaultEncodedPeriod = useMemo(
    () => encodePeriodValue(getCurrentReviewEntriesPeriodValue(), organization?.id ?? '') ?? '',
    [organization?.id],
  )
  const defaultQuery = useMemo<ReviewEntriesPeriodQuery>(
    () => ({ period: getReviewEntriesPeriodFromUrl() || defaultEncodedPeriod }),
    [defaultEncodedPeriod],
  )

  const [{ period }, setQuery] = useQueryParams<ReviewEntriesPeriodQuery>({ defaultQuery })

  const encodedPeriod = period || defaultEncodedPeriod
  const dateRange = useMemo(() => getReviewEntriesDateRangeFromEncodedPeriod(encodedPeriod), [encodedPeriod])

  const setPeriod = useCallback(
    (value: PeriodValue): boolean => {
      if (value.mode === PeriodMode.FiscalYear && !organization?.id) {
        return false
      }

      setQuery({ period: encodePeriodValue(value, organization?.id ?? '') ?? defaultEncodedPeriod })

      return true
    },
    [defaultEncodedPeriod, organization?.id, setQuery],
  )

  return {
    dateRange,
    encodedPeriod,
    setPeriod,
  }
}
